'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetThesesRow, useGetThesesInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ThesesHowItWorksModal } from '@/components/how-it-works/theses-how-it-works-modal';
import { ThesesEmpty } from '@/components/portfolio/empty-states/theses-empty';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { UserThesesSection } from '@/components/portfolio/tabs/user-theses-section';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { cn } from '@/lib/ui/utils';
import { isThesisAttributedToWallet } from '@/lib/wallet/multi-wallet-scope';
interface PortfolioThesesPanelProps {
  className?: string;
  attributionWalletAddress: string | null;
}
function ThesesInner(t0) {
  const $ = _c(9);
  const {
    userId,
    walletPubkeys,
    isOwn,
    attributionWalletAddress
  } = t0;
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      authorId: userId,
      walletPubkeys,
      sort: "recent"
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useGetThesesInfinite(t1);
  const rows = useLiveListData(query.queryKey, query.rows);
  const visibleRows = attributionWalletAddress === null ? rows : rows.filter(row => isThesisAttributedToWallet(row, attributionWalletAddress));
  if (!query.isPending && visibleRows.length === 0 && query.hasNextPage !== true) {
    let t2;
    if ($[3] !== isOwn) {
      t2 = <ThesesEmpty isOwn={isOwn} unframed={true} />;
      $[3] = isOwn;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== attributionWalletAddress || $[6] !== userId || $[7] !== walletPubkeys) {
    t2 = <UserThesesSection userId={userId} walletPubkeys={walletPubkeys} attributionWalletAddress={attributionWalletAddress} />;
    $[5] = attributionWalletAddress;
    $[6] = userId;
    $[7] = walletPubkeys;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
export function PortfolioThesesPanel(t0) {
  const $ = _c(29);
  const {
    className,
    attributionWalletAddress
  } = t0;
  const t = useTranslations("profile.stats");
  const tHowItWorks = useTranslations("theses_how_it_works");
  const {
    userId,
    viewerMode,
    walletPubkeys
  } = usePortfolioContext();
  const isViewerOwn = viewerMode === "own";
  const [helpOpen, setHelpOpen] = useState(false);
  let t1;
  if ($[0] !== tHowItWorks) {
    t1 = tHowItWorks("title");
    $[0] = tHowItWorks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setHelpOpen(true);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== helpOpen || $[4] !== t1) {
    t3 = {
      label: t1,
      onClick: t2,
      expanded: helpOpen
    };
    $[3] = helpOpen;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const infoTrigger = t3;
  let t4;
  if ($[6] !== className) {
    t4 = cn("min-w-0", className);
    $[6] = className;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("theses_title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== infoTrigger || $[11] !== t5) {
    t6 = <PortfolioPanelHeader title={t5} info={infoTrigger} />;
    $[10] = infoTrigger;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== attributionWalletAddress || $[14] !== isViewerOwn || $[15] !== userId || $[16] !== walletPubkeys) {
    t7 = <ThesesInner userId={userId} walletPubkeys={walletPubkeys} isOwn={isViewerOwn} attributionWalletAddress={attributionWalletAddress} />;
    $[13] = attributionWalletAddress;
    $[14] = isViewerOwn;
    $[15] = userId;
    $[16] = walletPubkeys;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <PanelContent className="flex flex-col gap-4 pb-0">{t6}{t7}</PanelContent>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t8) {
    t9 = <Panel className={t4}>{t8}</Panel>;
    $[21] = t4;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== helpOpen) {
    t10 = <ThesesHowItWorksModal open={helpOpen} onOpenChange={setHelpOpen} />;
    $[24] = helpOpen;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}